import { clone, merge, isNumber, cloneDeep, isFinite } from 'lodash';
import {
  PriceAdjustmentType,
  ProductSource,
  SelectedProduct,
  ProductCategory,
  Bundle,
  ProductRate,
  CoverageNameAndDeductibleOption,
  BundlePrices,
  ProviderProduct,
  UserProduct,
} from '../legos/penTypes';
import { fmtMoney } from '../formatter';
import { getPeriodicPayment, isOnePayLease } from '../payment-utils';
import {
  FinanceType,
  Origin,
  ProtectionPlansSelectionType,
} from '../constants';
import { isCash } from '../validation/financeType';
import { Car } from '../legos/carTypes';

// GAP Insurance
const GAP_SEARCH_WORDS = ['gap'];

// @TODO add proper types
type Deal = any;
type Dealer = any;
export type Product = any;
export type Overrides = Record<string, number>;
type ProductMap = any;
type SelectedTermMap = any;
type Condition = string | null;

// These products allow selection just to up to coverageName. Deductibles are selected on the fly
const productUsesFlexibleDeductible: Record<number, boolean> = {
  1605: true, // TFS Vehicle Service Agreement - production
  1284: true, // TFS Vehicle Service Agreement - testing
  1607: true, // TFS GAP - production
  1286: true, // TFS GAP - testing
  1609: true, // TFS Prepaid Maintenance  - production
  1288: true, // TFS Prepaid Maintenance  - testing
  1610: true, // TFS Excess Wear and Use  - production
  1289: true, // TFS Excess Wear and Use  - testing
};

export const isProductWithFlexibleDeductible = (product: Product) => {
  if (product.raw && product.raw.productId) {
    return productUsesFlexibleDeductible[product.raw.productId as number];
  }
  return false;
};

const getRatesFromSelectedProduct = (product: Product) => {
  if (!!product.rates && product.rates.length > 0) {
    return product.rates[0];
  }
  return product;
};

// Returns Product Price given selected term option
const getRelevantProductPrice = (
  product: Product,
  deal: Deal,
  selectedTermMap: SelectedTermMap,
) => {
  const tempProduct = cloneDeep(product);

  let rateObject =
    selectedTermMap && selectedTermMap[tempProduct.productId]
      ? selectedTermMap[tempProduct.productId]
      : getRatesFromSelectedProduct(tempProduct);

  // Check if bundlePricing is available for this product
  if (tempProduct.bundlePrices && deal?.selectedBundleIds?.length > 0) {
    Object.keys(tempProduct.bundlePrices).some(bundleId => {
      if (deal.selectedBundleIds.indexOf(bundleId) > -1) {
        // This product has bundle pricing
        // Update rateObject with bundlePricing
        rateObject = merge(rateObject, tempProduct.bundlePrices[bundleId]);
        return true;
      }
      return false;
    });
  }

  return deal.financeType && deal.financeType !== FinanceType.CASH
    ? rateObject.monthlyPayment
    : rateObject.retailPrice;
};

// Returns the calculated Price for a given product
// Considers configured markup and discount
// Respects min and max pricing rules prescribed by PEN
// (as provided by backend)
export const showProductPrice = (
  dealer: Dealer,
  deal: Deal,
  product: Product,
  selectedTermMap: SelectedTermMap,
  condition: Condition,
) => {
  if (deal && deal.car_id) {
    const { productId } = product;
    let price = getRelevantProductPrice(product, deal, selectedTermMap);
    let displayPrice;

    if (deal?.selectedBundleIds?.length > 0 && condition) {
      deal.selectedBundleIds.some(() => {
        return dealer.penConfig.bundles[condition][deal.financeType].some(
          (bundle: Bundle) => {
            // FIXME: is `bundle.discount` actually a valid field?
            const { discount } = bundle as any;
            if (bundle.productIds.indexOf(productId) >= 0) {
              price -= price * (discount / 100);
              return true;
            }
            return false;
          },
        );
      });
    }

    // Ensure no values less than $1 are displayed,
    // unless price is explicitly set to $0
    if (price < 1 && price !== 0) {
      price = 1;
    }

    const onePayLease = isOnePayLease({
      deal,
      dealer,
    });

    if (deal.financeType !== FinanceType.CASH && !onePayLease) {
      displayPrice = getPeriodicPayment({
        dealer,
        offer: { price },
      }).payment;
    }
    if (deal.financeType === FinanceType.CASH || onePayLease) {
      displayPrice = fmtMoney(price);
    }
    return { price, displayPrice };
  }
  return {};
};

// Returns the price and the effective discount
// of a Protection Plan Bundle
// Considers configured markup and discounts of
// all bundled products and bundle
// Respects min and max pricing rules prescribed by PEN
// (as provided by backend)
export const displayBundlePrice = (
  dealer: Dealer,
  deal: Deal,
  bundle: Bundle,
  productMap: ProductMap,
  selectedTermMap: SelectedTermMap,
) => {
  const onePayLease = isOnePayLease({
    deal,
    dealer,
  });
  const monthlyPaymentApplicable =
    deal.financeType && !isCash(deal?.financeType) && !onePayLease;
  const pricingDisplayKey = monthlyPaymentApplicable
    ? 'monthlyPayment'
    : 'retailPrice';
  let dynamicProductTotal = 0;
  let unaffectedProductTotal = 0;
  let monthlyProductTotal = 0;
  let retailProductTotal = 0;

  if (bundle && bundle.productIds) {
    const dynamicProductPrices = bundle.productIds.map((productId: string) => {
      if (productMap[productId] && productMap[productId].bundlePrices) {
        // If MonthlyPayment is found, apply APR
        const dynamicPrice = clone(
          productMap[productId].bundlePrices[bundle.id],
        );
        if (
          productMap[productId].bundlePrices[bundle.id] &&
          productMap[productId].bundlePrices[bundle.id].monthlyPayment
        ) {
          dynamicPrice.monthlyPayment = showProductPrice(
            dealer,
            deal,
            productMap[productId].bundlePrices[bundle.id],
            selectedTermMap,
            null,
          ).price;
        }
        return dynamicPrice;
      }
      return undefined;
    });

    if (dynamicProductPrices.length > 0) {
      dynamicProductPrices.forEach((dynamicPrice: any) => {
        if (dynamicPrice && dynamicPrice[pricingDisplayKey]) {
          dynamicProductTotal += dynamicPrice[pricingDisplayKey];
          monthlyProductTotal += dynamicPrice?.monthlyPayment || 0;
          retailProductTotal += dynamicPrice.retailPrice;
        }
      });
      dynamicProductPrices.forEach((dynamicPrice: any) => {
        if (
          dynamicPrice &&
          dynamicPrice.totalEffectiveDiscountPercentage >= 0 &&
          dynamicPrice.retailPrice >= 0
        ) {
          unaffectedProductTotal +=
            dynamicPrice.retailPrice /
            (1 - dynamicPrice.totalEffectiveDiscountPercentage / 100);
        }
      });
    }
  }

  // Ensure values between $0.01 and $0.99 are rounded to 1
  if (dynamicProductTotal >= 0.01 && dynamicProductTotal < 1) {
    dynamicProductTotal = 1;
    // Ensure no values less than $0.01 are displayed.
  } else if (dynamicProductTotal < 0.01) {
    dynamicProductTotal = 0;
  }

  const discount = Math.round(
    (1 - retailProductTotal / unaffectedProductTotal) * 100,
  );

  return {
    price: dynamicProductTotal,
    retail: retailProductTotal,
    monthly: monthlyProductTotal,
    discount: isFinite(discount) ? discount : 0,
  };
};

export const isInStoreDeal = (deal?: Deal): boolean => {
  return (
    !!deal?.origin && [Origin.INSTORE, Origin.DESKING].includes(deal.origin)
  );
};

// Return the Stored Markup Value
// for a Given Product or Bundle
export const getMarkup = (
  item: Product | Bundle | undefined,
  deal?: Deal,
): number => {
  return isInStoreDeal(deal)
    ? item?.markups?.[Origin.INSTORE] ?? 0
    : item?.markups?.[Origin.ONLINE] ?? 0;
};

// Return the Stored Discount Value
// for a Given Product or Bundle
export const getDiscount = (
  item: Product | Bundle | undefined,
  deal?: Deal,
): number => {
  return isInStoreDeal(deal)
    ? item?.discounts?.[Origin.INSTORE] ?? 0
    : item?.discounts?.[Origin.ONLINE] ?? 0;
};

// Return the Effective Discount Percentage
// for a Given Product
export const getEffectiveDiscountPercentage = (
  item: Product | Bundle | undefined,
  deal?: Deal,
): number => {
  // If this is a PEN Product,
  // and there is an effectiveDiscountPercentage available
  // simply return that value
  if (
    item?.source === ProductSource.PROVIDER &&
    item?.rates?.[0]?.effectiveDiscountPercentage
  ) {
    return Math.round(item?.rates?.[0]?.effectiveDiscountPercentage);
  }

  // If this is NOT a PEN product, or if there is no effectiveDiscountPercentage
  // we will return discount based on the products configuration

  // If the product discount is configured with Percent
  // Simply return that Percent value
  if (
    !item.priceAdjustmentType ||
    item.priceAdjustmentType === PriceAdjustmentType.PERCENT
  ) {
    return isInStoreDeal(deal)
      ? Math.round(item?.discounts?.[Origin.INSTORE]) ?? 0
      : Math.round(item?.discounts?.[Origin.ONLINE]) ?? 0;
  }

  const retailPrice = item?.rates?.[0]?.retailPrice
    ? item?.rates[0]?.retailPrice
    : item?.retailPrice;

  // If the product discount is configured with a Number
  // Compute the valid discount by comparing Retail Price with Discount
  // Discount = (100 * Discount) / Price
  // Note that at this point rates[0].retailPrice already includes discount and markup
  // Therefore we must accomodate for those adjustments in the below calculation
  const computedDiscount = isInStoreDeal(deal)
    ? (100 * (parseFloat(item?.discounts?.[Origin.INSTORE]) || 0)) /
      ((parseFloat(retailPrice) || 0) +
        (parseFloat(item?.discounts?.[Origin.INSTORE]) || 0) -
        (parseFloat(item?.markups?.[Origin.INSTORE]) || 0))
    : (100 * (parseFloat(item?.discounts?.[Origin.ONLINE]) || 0)) /
      ((parseFloat(retailPrice) || 0) +
        (parseFloat(item?.discounts?.[Origin.ONLINE]) || 0) -
        (parseFloat(item?.markups?.[Origin.ONLINE]) || 0));

  return Math.round(computedDiscount);
};

// Check if a product is GAP
export const isGAPProduct = (product: SelectedProduct) => {
  const words = (product.displayName ?? '').split(' ');
  return words.some(w => GAP_SEARCH_WORDS.includes(w.toLowerCase()));
};

// Calculates a final price base on any markups/bundle discounts
export const calculateFinalPrice = (
  product?: SelectedProduct,
  selectedBundles?: Bundle[],
  protectionPlanProductPriceOverrides?: { [key: string]: number },
  deal?: Deal,
): number => {
  if (!product) {
    return 0;
  }

  if (isNumber(protectionPlanProductPriceOverrides?.[product.productId])) {
    return protectionPlanProductPriceOverrides?.[product.productId] || 0;
  }

  const productBundle = product.bundleId
    ? selectedBundles?.find(b => b.id === product.bundleId)
    : selectedBundles?.find(bundle =>
        bundle?.productIds?.includes(product.productId),
      );
  if (productBundle) {
    const retailPrice =
      product.retailPriceWithoutBundleDiscountApplied ||
      product.retailPrice ||
      0;
    const markup = getMarkup(productBundle, deal) / 100;
    const discount = getDiscount(productBundle, deal) / 100;
    const retailPriceWithMarkupDiscount =
      retailPrice * (1 + (markup - discount));
    return retailPriceWithMarkupDiscount;
  }
  return product.retailPrice || 0;
};

// Product categories that are not included in backend calculation
const nonBackendProductCategories = [
  ProductCategory.FRONT_END_ADD_ON,
  ProductCategory.INSURANCE,
];

/**
 * Helper function to determine if a provided product will be considered part
 * of the backend calculation depending on the category.
 *
 * Backend Calculation Version 1: GAP and Optional Service Contracts
 *   - based on current behavior, includes any product that doesn't have a
 *     category or has a category that is NOT a Front end add on or Insurance
 *
 * @param product - product with `productCategory` field
 * @param backendCalculationVersion - current backend calculation version provided by DAPI
 * @returns boolean
 */
export function isBackendProduct(
  product: { productCategory?: ProductCategory },
  backendCalculationVersion?: number,
): boolean {
  if (backendCalculationVersion === 1) {
    return (
      !product.productCategory ||
      !nonBackendProductCategories.includes(product.productCategory)
    );
  }
  return false;
}

export function getInStoreSelection(
  bundleOrProduct: Pick<
    Bundle,
    'selectedInStore' | 'defaultSelectedInStore' | 'mandatory'
  >,
): ProtectionPlansSelectionType {
  if (bundleOrProduct.selectedInStore !== undefined) {
    return bundleOrProduct.selectedInStore;
  }

  if (bundleOrProduct.mandatory) {
    return ProtectionPlansSelectionType.Mandatory;
  }

  if (bundleOrProduct.defaultSelectedInStore) {
    return ProtectionPlansSelectionType.Default;
  }

  return ProtectionPlansSelectionType.None;
}

export function getOnlineSelection(
  bundleOrProduct: Pick<Bundle, 'selectedOnline' | 'defaultSelectedOnline'>,
): ProtectionPlansSelectionType {
  if (bundleOrProduct.selectedOnline !== undefined) {
    return bundleOrProduct.selectedOnline;
  }

  if (bundleOrProduct.defaultSelectedOnline) {
    return ProtectionPlansSelectionType.Default;
  }

  return ProtectionPlansSelectionType.None;
}

/**
 * @description When a selected product is found in more than one selected bundles
 * this function will find the best bundle to associate with the product. Currently
 * "best" is defined as the bundle with the best discount on the product.
 * @param bundlePricing A map of bundle ID to the products pricing in that bundle.
 * The price should include any discounts offered by the bundle.
 */
export function getBestSelectedBundleForProduct(
  bundlePricing: Record<
    string,
    {
      retailPrice: number;
    }
  >,
  selectedBundleIds: string[],
): string | null {
  let cheapestBundleId: string | null = null;
  let cheapestBundlePrice = Infinity;
  const bundleIdSet = new Set(selectedBundleIds);

  Object.entries(bundlePricing).forEach(
    ([bundleId, productPricingInBundle]) => {
      if (!bundleIdSet.has(bundleId)) {
        return;
      }

      if (
        cheapestBundleId === null ||
        productPricingInBundle.retailPrice < cheapestBundlePrice
      ) {
        cheapestBundleId = bundleId;
        cheapestBundlePrice = productPricingInBundle.retailPrice;
      }
    },
  );

  return cheapestBundleId;
}

function isSubset(arr1: string[], arr2: string[]): boolean {
  const arr2Set = new Set(arr2);
  return arr1.every(item => arr2Set.has(item));
}

function isSubBundle(
  b1: Pick<Bundle, 'id' | 'productIds'>,
  b2: Pick<Bundle, 'id' | 'productIds'>,
): boolean {
  if (b1.id === b2.id) {
    return false;
  }

  return isSubset(b1.productIds, b2.productIds);
}

/**
 * @description Given a list of selected product IDs and available bundles, determine
 * what bundles should be selected. This prefers larger bundles when 2 bundles
 * are a super/subset of each other.
 */
export function getSelectedBundlesFromProducts<
  BundleType extends { id: string; productIds: string[] }
>(selectedProductIds: string[], bundles: BundleType[]): BundleType[] {
  const bundlesWithAllProductsSelected = bundles.filter(bundle =>
    bundle.productIds.every(pid => selectedProductIds.includes(pid)),
  );

  // remove selected bundles that are a complete subset of another bundle
  const subsetsRemoved = bundlesWithAllProductsSelected.filter(
    bundleToCheck => {
      const isSubsetOfAnotherBundle = bundlesWithAllProductsSelected.some(b =>
        isSubBundle(bundleToCheck, b),
      );

      return !isSubsetOfAnotherBundle;
    },
  );

  return subsetsRemoved;
}

export const findClosestRate = ({
  rates,
  term,
}: {
  rates: ProductRate[];
  term: {
    value: {
      termMiles?: number;
      termMonths?: number;
      coverageName?: string;
    };
  };
}): ProductRate => {
  const { termMonths, termMiles, coverageName } = term.value;
  let filteredRate = rates;

  // filter out rates that don't match the coverageName
  if (coverageName) {
    filteredRate = rates.filter(rate => rate.coverageName === coverageName);
  }

  // Check if there's an exact match for term months
  const exactMatchRate = filteredRate.find(rate => {
    const rateMonths = rate.termMonths || rate.term;
    const rateMiles = rate.termMiles || rate.miles;
    return rateMonths === termMonths && rateMiles === termMiles;
  });
  if (exactMatchRate) {
    return exactMatchRate;
  }

  let closestRate = filteredRate[0] || {};
  let smallestDifference = Infinity;

  filteredRate?.forEach(rate => {
    const rateMonths = rate.termMonths || rate.term;
    const rateMiles = rate.termMiles || rate.miles;
    if (termMonths !== undefined && rateMonths !== undefined) {
      // Find the closest termMonths
      const difference = Math.abs(termMonths - rateMonths);
      if (difference < smallestDifference) {
        closestRate = rate;
        smallestDifference = difference;
      }
    } else if (termMiles !== undefined && rateMiles !== undefined) {
      // Find the closest termMiles
      const difference = Math.abs(termMiles - rateMiles);
      if (difference < smallestDifference) {
        closestRate = rate;
        smallestDifference = difference;
      }
    }
  });
  return closestRate;
};

export const isEligibleCoverage = ({
  coverageOption,
  car,
}: {
  coverageOption: CoverageNameAndDeductibleOption;
  car: Car;
}): boolean => {
  const { year, mileage } = car;
  const {
    minAge,
    maxAge,
    minMileage,
    maxMileage,
    instoreEnabled,
  } = coverageOption;
  if (!instoreEnabled) {
    return false;
  }

  if (minAge && year) {
    const carAge = new Date().getFullYear() - year;
    if (carAge < minAge) {
      return false;
    }
  }

  if (maxAge && year) {
    const carAge = new Date().getFullYear() - year;
    if (carAge > maxAge) {
      return false;
    }
  }

  if (minMileage && mileage && mileage < minMileage) {
    return false;
  }

  if (maxMileage && mileage && mileage > maxMileage) {
    return false;
  }

  return true;
};

export const getDefaultRate = (
  rates: ProductRate[],
): ProductRate | undefined => {
  return (
    rates?.find(r => !!r.isDefaultCoverageName && !!r.isDefault) ||
    rates?.find(r => !!r.isDefaultCoverageName) ||
    (rates.length > 0 ? rates[0] : undefined)
  );
};

export interface TermOption {
  term: number;
  miles: number;
  deductible?: {
    monthlyPayment?: number;
    retailPrice?: number;
    minRetailPrice?: number;
    maxRetailPrice?: number;
  };
  bundlePrices?: BundlePrices;
  renderValue?: string;
  coverageName?: string;
  coverageDisplayName?: string;
}

export function findMatchedRate<T extends TermOption | ProductRate>({
  rates,
  coverageName,
  termMonths,
  termMiles,
}: {
  rates: T[];
  coverageName?: string;
  termMonths?: number;
  termMiles?: number;
}): T | undefined {
  // Find exact match
  const exactMatch = rates.find(rate => {
    return (
      rate.coverageName === coverageName &&
      rate.term === termMonths &&
      rate.miles === termMiles
    );
  });
  if (exactMatch) {
    return exactMatch;
  }

  // Find coverage & term match
  const coverageAndTermMatch = rates.find(rate => {
    return rate.coverageName === coverageName && rate.term === termMonths;
  });
  if (coverageAndTermMatch) {
    return coverageAndTermMatch;
  }

  // Find closest term match with same coverage
  const coverageMatches = rates.filter(
    rate => rate.coverageName === coverageName && rate.term,
  );
  if (coverageMatches.length > 0 && termMonths) {
    let closestTermMatch = coverageMatches[0];

    coverageMatches.forEach(rate => {
      if (!rate.term || !closestTermMatch.term) return;
      const currentDiff = Math.abs(rate.term - termMonths);
      const closestDiff = Math.abs(closestTermMatch.term - termMonths);
      if (currentDiff < closestDiff) {
        closestTermMatch = rate;
      }
    });

    if (closestTermMatch) {
      return closestTermMatch;
    }
  }

  // Find coverage name only match
  const coverageOnlyMatch = rates.find(
    rate => rate.coverageName === coverageName,
  );
  if (coverageOnlyMatch) {
    return coverageOnlyMatch;
  }

  // Find term/mile match as last resort
  return rates.find(
    rate => rate.term === termMonths && rate.miles === termMiles,
  );
}

/** Type-guard function for {@link ProviderProduct}. */
export function isProviderProduct(p: Product): p is ProviderProduct {
  return p.source === ProductSource.PROVIDER;
}

/** Type-guard function for {@link UserProduct}. */
export function isUserProduct(p: Product): p is UserProduct {
  return p.source === ProductSource.USER;
}
